// src/components/education/periodic-table/PeriodicTableApp2D.tsx
// Updated to match Landing page styling and structure
import React, { useState } from 'react';
import styled from 'styled-components';
import PeriodicTable2D from './PeriodicTable2D';
import ElementDetail from './ElementDetail';
import MaterialsExplorer from './MaterialsExplorer';
import { ElementData } from './Element';

// Define the highlight group
interface HighlightGroup {
  id: string;
  name: string;
  elements: string[];
}

// Only keep the building materials highlight group
const highlightGroups: HighlightGroup[] = [
  {
    id: 'building-materials',
    name: 'Common Building Materials',
    elements: ['C', 'O', 'Si', 'Al', 'Fe', 'Ca', 'Na', 'K', 'Mg', 'H', 'Cl', 'S', 'Ti', 'Mn', 'Cu', 'Zn', 'Pb', 'N', 'P', 'F', 'B', 'Sn', 'Ni', 'Cr', 'V', 'Mo', 'W', 'Co', 'Ag', 'Au']
  }
];

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  z-index: 1;
  margin-top: 20px;
  gap: 2rem;
  overflow: hidden;
`;

const PeriodicTableWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  min-width: 0;
`;

const MaterialsExplorerWrapper = styled.div`
  width: 350px;
  margin-left: 2rem;
  flex-shrink: 0;
`;

const HighlightingOptions = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const HighlightButton = styled.button<{ isActive: boolean }>`
  padding: 8px 16px;
  background-color: ${props => props.isActive ? 'rgba(255, 165, 0, 0.7)' : 'rgba(75, 75, 75, 0.5)'};
  color: white;
  border: 1px solid ${props => props.isActive ? 'orange' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: ${props => props.isActive ? 'bold' : 'normal'};
  box-shadow: ${props => props.isActive ? '0 0 15px rgba(255, 165, 0, 0.3)' : 'none'};
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.8);
  
  &:hover {
    background-color: ${props => props.isActive ? 'rgba(255, 165, 0, 0.8)' : 'rgba(100, 100, 100, 0.6)'};
    border-color: ${props => props.isActive ? 'orange' : 'rgba(255, 255, 255, 0.5)'};
  }
`;

const PeriodicTableApp2D: React.FC = () => {
  const [selectedElement, setSelectedElement] = useState<ElementData | null>(null);
  const [highlightedElements, setHighlightedElements] = useState<string[]>([]);
  const [activeHighlightGroup, setActiveHighlightGroup] = useState<string | null>(null);
  const [materialHighlightedElements, setMaterialHighlightedElements] = useState<string[]>([]);

  const handleElementClick = (element: ElementData) => {
    setSelectedElement(element);
  };

  const handleHighlightButtonClick = (groupId: string) => {
    if (activeHighlightGroup === groupId) {
      // Toggle off
      setActiveHighlightGroup(null);
      setHighlightedElements([]);
    } else {
      // Toggle on
      setActiveHighlightGroup(groupId);
      const group = highlightGroups.find(g => g.id === groupId);
      if (group) {
        setHighlightedElements(group.elements);
      }
    }
  };

  const handleMaterialSelect = (elementSymbols: string[]) => {
    setMaterialHighlightedElements(elementSymbols);
  };

  // Combine highlight sources
  const combinedHighlightedElements = [...new Set([...highlightedElements, ...materialHighlightedElements])];

  return (
    <Container>
      <HighlightingOptions>
        {highlightGroups.map(group => (
          <HighlightButton
            key={group.id}
            isActive={activeHighlightGroup === group.id}
            onClick={() => handleHighlightButtonClick(group.id)}
          >
            {group.name}
          </HighlightButton>
        ))}
      </HighlightingOptions>
      
      <ContentWrapper>
        <PeriodicTableWrapper>
          <PeriodicTable2D
            onElementSelect={handleElementClick}
            highlightedElements={combinedHighlightedElements}
          />
        </PeriodicTableWrapper>
        
        <MaterialsExplorerWrapper>
          <MaterialsExplorer onMaterialSelect={handleMaterialSelect} />
        </MaterialsExplorerWrapper>
      </ContentWrapper>

      {selectedElement && (
        <ElementDetail
          element={selectedElement}
          onClose={() => setSelectedElement(null)}
        />
      )}
    </Container>
  );
};

export default PeriodicTableApp2D; 